import { Badge, Button, Card, CardContent, CardHeader } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { formatDateTimeShort } from "@/shared/lib/date-time";

type OrderCardProps = {
  order: {
    id: string;
    orderNo: number | string;
    orderType: string;
    status: string;
    tableName?: string | null;
    salespersonName?: string | null;
    customerDisplay?: string | null;
    grandTotal: number;
    createdAt: string | Date;
    isLocal?: boolean;
    syncStatus?: string;
    lastSyncError?: string;
    isCarryOverFromPriorShift?: boolean;
    isPaidInCurrentShift?: boolean;
  };
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "OPEN":
      return "bg-blue-100 text-blue-800";
    case "PAID":
      return "bg-green-100 text-green-800";
    case "VOID":
      return "bg-red-100 text-red-800";
    case "REFUNDED":
      return "bg-orange-100 text-orange-800";
    case "PARTIAL_REFUNDED":
      return "bg-amber-100 text-amber-800";
    case "MERGED":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "OPEN":
      return "ເປີດ";
    case "PAID":
      return "ຊຳລະແລ້ວ";
    case "VOID":
      return "ຍົກເລີກ";
    case "REFUNDED":
      return "ຄືນເງິນ";
    case "PARTIAL_REFUNDED":
      return "ຄືນເງິນບາງສ່ວນ";
    case "MERGED":
      return "ລວມບິນ";
    default:
      return status;
  }
};

const getOrderTypeLabel = (orderType: string) => {
  switch (orderType) {
    case "DINE_IN":
      return "ກິນໃນຮ້ານ";
    case "TAKEAWAY":
      return "ສັ່ງກັບບ້ານ";
    case "DELIVERY":
      return "ຈັດສົ່ງ";
    default:
      return orderType;
  }
};

const getSyncStatusLabel = (status?: string) => {
  switch (status) {
    case "not_ready":
      return "Local only";
    case "queued":
      return "ລໍຖ້າ sync";
    case "syncing":
      return "ກຳລັງ sync";
    case "synced":
      return "sync ແລ້ວ";
    case "failed":
      return "sync failed";
    case "needs_review":
      return "needs review";
    default:
      return status ?? "Local";
  }
};

const getSyncStatusColor = (status?: string) => {
  switch (status) {
    case "queued":
      return "border-amber-300 bg-amber-50 text-amber-800";
    case "syncing":
      return "border-blue-300 bg-blue-50 text-blue-800";
    case "synced":
      return "border-green-300 bg-green-50 text-green-800";
    case "failed":
    case "needs_review":
      return "border-red-300 bg-red-50 text-red-800";
    default:
      return "border-primary/30 bg-primary/5 text-primary";
  }
};

export function OrderCard({ order }: OrderCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate({
      to: "/app/pos/orders/$orderId",
      params: { orderId: order.id },
    });
  };

  return (
    <Card className="cursor-pointer transition-all hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">#{order.orderNo}</h3>
            {order.isCarryOverFromPriorShift && (
              <Badge
                variant="outline"
                className="mt-1 border-amber-300 bg-amber-50 text-amber-900 text-xs dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
              >
                ຄ້າງຈາກກະກ່ອນ
              </Badge>
            )}
            {order.isPaidInCurrentShift && (
              <Badge
                variant="outline"
                className="mt-1 border-green-300 bg-green-50 text-green-900 text-xs dark:border-green-800 dark:bg-green-950/40 dark:text-green-200"
              >
                ຈ່າຍໃນກະນີ້
              </Badge>
            )}
            {order.isLocal && (
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <Badge variant="outline" className="text-xs">
                  Local order
                </Badge>
                <Badge
                  variant="outline"
                  className={`text-xs ${getSyncStatusColor(order.syncStatus)}`}
                >
                  {getSyncStatusLabel(order.syncStatus)}
                </Badge>
              </div>
            )}
            <p className="text-muted-foreground text-sm">
              {formatDateTimeShort(order.createdAt)}
            </p>
          </div>
          <Badge className={getStatusColor(order.status)}>
            {getStatusLabel(order.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">ປະເພດ:</span>
          <Badge variant="outline">{getOrderTypeLabel(order.orderType)}</Badge>
        </div>
        {order.tableName && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">ໂຕະ:</span>
            <span className="font-medium">{order.tableName}</span>
          </div>
        )}
        {order.salespersonName && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">ຄົນຂາຍ:</span>
            <span className="font-medium">{order.salespersonName}</span>
          </div>
        )}
        {order.customerDisplay && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">ລູກຄ້າ:</span>
            <span className="max-w-[60%] truncate text-right font-medium">
              {order.customerDisplay}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between border-t pt-2">
          <span className="font-semibold text-base">ລາຄາລວມ:</span>
          <span className="font-bold text-lg text-primary">
            {order.grandTotal.toLocaleString("lo-LA")} ₭
          </span>
        </div>
        <Button className="mt-2 w-full" onClick={handleClick}>
          ເບິ່ງລາຍລະອຽດ
        </Button>
      </CardContent>
    </Card>
  );
}
